import { useCallback, useContext, useMemo, useState } from 'react';
import { Box, Overlay } from '@alf/uikit';
import {
  PersonalOfficeWrapper,
  PhoneAndEmailVerification
} from '@namespace/account';
import { CommonLoader } from '@namespace/common';
import {
  FORM_VALIDATION_TYPES,
  FormBuilder,
  useFormMethods
} from '@namespace/forms';
import { useI18n } from '@namespace/i18n';
import { useNotifications } from '@namespace/notifications';
import { useGetPepAttributes, UserContext } from '@namespace/user';
import useProfileFormFields from './hooks/useProfileFormFields';

import styles from './index.module.css';

const phoneValidator = {
  type: FORM_VALIDATION_TYPES.MIN_LENGTH_AND_CHECKPHONE_VALIDATOR,
  parameters: [3, 'validators.requiredValidator', (v) => v.number]
};

const ProfileData = () => {
  const { successNotification, errorNotification } = useNotifications();
  const { t, f } = useI18n();
  const [isFetching, setIsFetching] = useState(false);

  const [user, userActions] = useContext(UserContext);
  const DATE_FORMATS_TYPES = f.getFormats();
  const {
    title,
    id,
    dateOfBirth,
    birthday,
    email,
    adress: address,
    city,
    accountStatuses = {},
    userOptions = {}
  } = user;
  const pepAttributes = useGetPepAttributes();
  const { pepGroundNumber = '', pepPosition: pepGroundPosition = '' } =
    userOptions.pepGround || {};

  const pepOptions = useMemo(
    () =>
      pepAttributes.map((attribute) => ({
        name: t(`personalOffice.profile.data.pepAttribute.${attribute}`),
        value: attribute
      })),
    [pepAttributes, t]
  );
  const { isPep } = accountStatuses;
  const { CHANGE_USER, FETCH_USER_DATA } = userActions;

  const defaultValues = {
    sex: title,
    birthday: f.fromMillis(
      birthday || dateOfBirth,
      DATE_FORMATS_TYPES.CLIENT_API
    ),
    email,
    phone: '',
    address,
    city,
    pep: isPep,
    pepGround: pepGroundNumber,
    pepPosition: pepGroundPosition
  };

  const methods = useFormMethods({ defaultValues });
  const { watch } = methods;
  const watchPep = watch('pep');

  const componentsList = useProfileFormFields({
    isCurrentPepChecked: watchPep,
    isFetching,
    isInitPepChecked: isPep,
    pepOptions
  });

  const submit = useCallback(
    async ({ pep, pepGround, pepPosition, phone: newPhone, data }) => {
      setIsFetching(true);

      const pepData = {
        pep_ground: pepGround,
        pep_position: pepPosition
      };

      const phone = newPhone || undefined;

      try {
        const dataInfo = {
          ...data,
          statuses: pep ? ['PEP'] : [],
          id,
          phone,
          ...pepData
        };

        await CHANGE_USER(dataInfo);

        successNotification(t('profile.data.profileSaved'));
        FETCH_USER_DATA();
      } catch ({ errorCode }) {
        errorNotification(t(`accountingMessages.${errorCode}`));
      }

      setIsFetching(false);
    },
    [
      FETCH_USER_DATA,
      setIsFetching,
      CHANGE_USER,
      successNotification,
      errorNotification,
      t
    ]
  );

  return (
    <Box component={PersonalOfficeWrapper} direction="column">
      {isFetching && (
        <Overlay>
          <CommonLoader />
        </Overlay>
      )}
      <Box className={styles.form} direction="column">
        <PhoneAndEmailVerification
          classNames={{ formWrapperClassName: styles.verificationFields }}
          minNumberLength={3}
          phoneValidator={phoneValidator}
          regPhoneProps={{
            preventCodeChange: true,
            defaultCountry: 'RO'
          }}
        />

        <FormBuilder
          components={componentsList}
          formConfig={{ defaultValues }}
          formMethods={methods}
          onSubmit={submit}
          formProps={{}}
        />
      </Box>
    </Box>
  );
};

export default ProfileData;
